import { useCallback } from "react";
import { addEdge } from "@xyflow/react";
import { notifyIcon, quote } from "../../../../config";
import { getNotification } from "../../../../utils/common";
import { NODE_LABELS, NODE_TYPES, OUTPUT_NODES, SOURCE_HANDLES, STARTER_NODE_UI_ID } from "../ruleBasedFlow.constants";
import { createAddEdgePayload, createAddTaskEdgePayload } from "../ruleBasedFlow.helpers";
import * as ruleBasedFlowService from "../ruleBasedFlow.service";

/**
 * Custom hook for edge operations (create, update, delete)
 * @param {string} entityId - Workflow ID (workflow context) or Task ID (task context)
 * @param {string} context - "workflow" | "task"
 * @param {Array} nodes - Array of nodes to validate connections
 * @param {Array} edges - Array of edges
 * @param {Function} setEdges - Function to update edges state
 * @param {Function} setNodes - Function to update nodes state
 * @param {Function} onAddNodeBetween - Callback for adding node between edges
 * @param {Function} setFirstNodeId - Function to set first node ID
 */
export const useEdgeOperations = (entityId, context, nodes, edges, setEdges, setNodes, onAddNodeBetween, setFirstNodeId) => {
  /**
   * Validates if a connection is allowed based on node types and handles
   */
  const validateConnection = useCallback(
    (sourceNodeId, targetNodeId, sourceHandle) => {
      const sourceNode = nodes.find((node) => node.id === sourceNodeId);
      const targetNode = nodes.find((node) => node.id === targetNodeId);

      if (!sourceNode || !targetNode) {
        return true;
      }

      if (sourceNode.type === NODE_TYPES.ASSIGN && sourceHandle === SOURCE_HANDLES.RIGHT) {
        if (!OUTPUT_NODES.includes(targetNode.type)) {
          const outputNodeNames = OUTPUT_NODES.map((type) => NODE_LABELS[type] || type).join(" or ");
          getNotification(`Only ${outputNodeNames} node can be connected here`, notifyIcon.WARNING_ICON);
          return false;
        }
      }

      return true;
    },
    [nodes]
  );

  /**
   * Handles deletion of the starter edge (UI-only).
   * Removes the edge from UI and updates the target node's isFirstNode flag in the backend.
   * @param {string} edgeId - Starter edge ID
   * @param {string} targetNodeId - Target node ID whose isFirstNode flag needs to be cleared
   */
  const deleteStarterEdge = useCallback(
    async (edgeId, targetNodeId) => {
      setEdges((eds) => eds.filter((edge) => edge.id !== edgeId));

      if (!targetNodeId || !entityId) return;

      try {
        const updateNodeResponse =
          context === "task"
            ? await ruleBasedFlowService.updateTaskNode({ nodeId: targetNodeId, taskId: parseInt(entityId), isFirstNode: false })
            : await ruleBasedFlowService.updateNode({ nodeId: targetNodeId, workflowId: parseInt(entityId), isFirstNode: false });

        if (!updateNodeResponse) {
          getNotification(quote.FAILED_TO_UPDATE_FIRST_NODE_STATUS, notifyIcon.ERROR_ICON);
          return;
        }

        setNodes((nds) =>
          nds.map((node) =>
            node.id === targetNodeId ? { ...node, data: { ...node.data, isFirstNode: false } } : node
          )
        );

        if (setFirstNodeId) setFirstNodeId(null);

        getNotification(quote.STARTER_CONNECTION_REMOVED, notifyIcon.SUCCESS_ICON);
      } catch (error) {
        getNotification(quote.FAILED_TO_UPDATE_FIRST_NODE_STATUS, notifyIcon.ERROR_ICON);
      }
    },
    [entityId, context]
  );

  /**
   * Handles deletion of a regular (non-starter) edge.
   * Removes the edge from UI and calls the backend to delete it.
   * @param {string} edgeId - Edge identifier (MongoDB _id)
   * @param {string} sourceNodeId - Source node ID
   * @param {string} targetNodeId - Target node ID
   */
  const deleteRegularEdge = useCallback(
    async (edgeId, sourceNodeId, targetNodeId) => {
      setEdges((eds) => eds.filter((edge) => edge.id !== edgeId));

      try {
        const response =
          context === "task"
            ? await ruleBasedFlowService.deleteTaskNodeEdge({ edgeId, taskId: parseInt(entityId), nodeId: sourceNodeId, target: targetNodeId })
            : await ruleBasedFlowService.deleteNodeEdge({ edgeId, nodeId: sourceNodeId, workflowId: parseInt(entityId), target: targetNodeId });

        if (!response) {
          getNotification("Failed to delete edge", notifyIcon.ERROR_ICON);
        }
      } catch (error) {
        getNotification("Failed to delete edge", notifyIcon.ERROR_ICON);
      }
    },
    [entityId, context]
  );

  /**
   * Deletes an edge from UI and backend.
   * Defined before onConnect so onConnect can reference it in edge data.
   * Delegates to deleteStarterEdge or deleteRegularEdge based on source node.
   * @param {string} edgeId - Edge identifier (MongoDB _id or UI edge ID)
   * @param {string} sourceNodeId - Source node ID
   * @param {string} targetNodeId - Target node ID
   */
  const deleteEdge = useCallback(
    async (edgeId, sourceNodeId, targetNodeId) => {
      if (sourceNodeId === STARTER_NODE_UI_ID) {
        await deleteStarterEdge(edgeId, targetNodeId);
      } else {
        await deleteRegularEdge(edgeId, sourceNodeId, targetNodeId);
      }
    },
    [deleteStarterEdge, deleteRegularEdge]
  );

  /**
   * Handles a new connection from the starter node (UI-only edge).
   * Enforces a single outgoing starter connection, updates UI optimistically,
   * and marks the target node as isFirstNode in the backend.
   * @param {Object} params - Connection params from React Flow
   */
  const connectStarterNode = useCallback(
    async (params) => {
      const existingStarterEdge = edges.find((edge) => edge.source === STARTER_NODE_UI_ID);
      if (existingStarterEdge) {
        getNotification("Starter node can only have one outgoing connection", notifyIcon.WARNING_ICON);
        return;
      }

      const starterEdgeId = `${STARTER_NODE_UI_ID}-${params.target}`;
      const starterEdge = {
        id: starterEdgeId,
        source: STARTER_NODE_UI_ID,
        target: params.target,
        sourceHandle: params.sourceHandle || SOURCE_HANDLES.BOTTOM,
        type: "interactive",
        animated: false,
        style: { stroke: "#1e3a8a" },
        data: {
          onAddNode: onAddNodeBetween,
          onDeleteEdge: deleteEdge,
          sourceHandle: params.sourceHandle || SOURCE_HANDLES.BOTTOM,
        },
      };

      setEdges((eds) => addEdge(starterEdge, eds));

      setNodes((nds) =>
        nds.map((node) =>
          node.id === params.target ? { ...node, data: { ...node.data, isFirstNode: true } } : node
        )
      );

      try {
        const response =
          context === "task"
            ? await ruleBasedFlowService.updateTaskNode({ nodeId: params.target, taskId: parseInt(entityId), isFirstNode: true })
            : await ruleBasedFlowService.updateNode({ nodeId: params.target, workflowId: parseInt(entityId), isFirstNode: true });

        if (!response) {
          getNotification("Failed to update first node status", notifyIcon.ERROR_ICON);
        }
      } catch (error) {
        getNotification("Failed to update first node status", notifyIcon.ERROR_ICON);
      }
    },
    [edges, entityId, context]
  );

  /**
   * Handles a new connection between two regular nodes.
   * Adds a temp edge optimistically, persists to backend, then replaces with the real edge ID.
   * @param {Object} params - Connection params from React Flow
   */
  const connectRegularNode = useCallback(
    async (params) => {
      const tempEdgeId = `${params.source}-${params.target}`;
      const edgeWithType = {
        ...params,
        id: tempEdgeId,
        type: "interactive",
        data: {
          onAddNode: onAddNodeBetween,
          onDeleteEdge: deleteEdge,
          sourceHandle: params.sourceHandle,
        },
      };
      setEdges((eds) => addEdge(edgeWithType, eds));

      if (!entityId) {
        setEdges((eds) => eds.filter((edge) => edge.id !== tempEdgeId));
        getNotification(`${context === "task" ? "TaskId" : "WorkflowId"} is missing`, notifyIcon.ERROR_ICON);
        return;
      }

      try {
        let edgeResponse;
        if (context === "task") {
          const edgePayload = createAddTaskEdgePayload(params.source, params.target, entityId, params.sourceHandle || SOURCE_HANDLES.BOTTOM);
          edgeResponse = await ruleBasedFlowService.addTaskNodeEdge(edgePayload);
        } else {
          const edgePayload = createAddEdgePayload(params.source, params.target, entityId, params.sourceHandle || SOURCE_HANDLES.BOTTOM);
          edgeResponse = await ruleBasedFlowService.addNodeEdge(edgePayload);
        }

        if (!edgeResponse || !edgeResponse._id) {
          setEdges((eds) => eds.filter((edge) => edge.id !== tempEdgeId));
          getNotification("Failed to save connection. Please try again.", notifyIcon.ERROR_ICON);
          return;
        }

        // Replace temp edge with real backend edge, keeping the real deleteEdge handler
        setEdges((eds) =>
          eds.map((edge) =>
            edge.id === tempEdgeId
              ? {
                  ...edge,
                  id: edgeResponse._id,
                  data: {
                    ...edge.data,
                    onAddNode: onAddNodeBetween,
                    onDeleteEdge: deleteEdge,
                    sourceHandle: edge.sourceHandle,
                  },
                }
              : edge
          )
        );
      } catch (error) {
        setEdges((eds) => eds.filter((edge) => edge.id !== tempEdgeId));
        getNotification("Failed to save connection. Please try again.", notifyIcon.ERROR_ICON);
      }
    },
    [entityId, context, deleteEdge]
  );

  /**
   * Handles manual edge connections via drag and drop.
   * Validates the connection then delegates to connectStarterNode or connectRegularNode.
   */
  const onConnect = useCallback(
    async (params) => {
      if (!validateConnection(params.source, params.target, params.sourceHandle)) {
        return;
      }

      if (params.source === STARTER_NODE_UI_ID) {
        await connectStarterNode(params);
      } else {
        await connectRegularNode(params);
      }
    },
    [connectStarterNode, connectRegularNode]
  );

  return { onConnect, deleteEdge };
};
